import { useRefundInvoiceMutation } from "./useRefundInvoiceMutation";
import type { IRefundInvoicePayload } from "../../create/interfaces/invoice.interfaces";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { AxiosError } from "axios";
import { useModals } from "../../../../context/ModalContext";

/**
 * Domain hook for refunding invoices (IWT compliant).
 * Maps 422 to backend message (e.g. insufficient balance) for seller-facing message.
 */
export const useRefundInvoice = () => {
  const mutation = useRefundInvoiceMutation();
  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const message = (error.response?.data as any)?.message ?? error.message;

      switch (status) {
        case 400:
          return message || "Invalid refund request.";
        case 403:
          return message || "You don't have permission to refund this invoice.";
        case 404:
          return message || "Invoice not found.";
        case 422:
          return message || "Refund could not be processed. Please try again.";
        case 500:
          return message || "An unexpected error occurred. Please try again.";
        default:
          return message || "An unexpected error occurred. Please try again.";
      }
    }
    if (error instanceof Error) return error.message;
    return "An unexpected error occurred. Please try again.";
  };

  const refundInvoice = (
    invoiceUid: string,
    options?: IRefundInvoicePayload & {
      billingJobEventUids?: string[];
      extraInvalidateJobEventUids?: string[];
      quoteId?: number | null;
      invoiceModal?: string;
    },
  ) => {
    const { amount, reason, billingJobEventUids, extraInvalidateJobEventUids, quoteId } = options ?? {};
    mutation.mutate(
      {
        invoiceUid,
        payload: amount != null || reason ? { amount, reason } : undefined,
        billingJobEventUids,
        extraInvalidateJobEventUids,
        quoteId,
      },
      {
        onSuccess: () => {
          showSnackbar("Refund processed successfully", "success", 5000, "top-center");
          if (options?.invoiceModal) {
            closeByKey(options.invoiceModal);
          }
        },
        onError: (error) => {
          showSnackbar(getErrorMessage(error), "error", 6000, "top-center");
        },
      },
    );
  };

  return {
    refundInvoice,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
  };
};
